import type { ReactNode } from 'react';
import { Badge, Box, InlineStack, Link, Stack } from '@customerio/pluma-components/react';
import { AttributeTypeLabel, type IdentifiersConfig } from '@/features/people';
import { AutogeneratedAttributeTooltip } from './autogenerated-attribute-tooltip';
import { RowClickGuard } from './row-click-guard';

/**
 * A row's name, what is known about it, and its description.
 *
 * The name leads to the item's own page. The description sits under it and
 * is edited in place, because it is the one thing about a row that the
 * workspace writes rather than the ingestion.
 */

export type NameCellProps = {
  name: string;
  href: string;
  isSensitive: boolean;
  isAutogenerated: boolean;
  /** Only person attributes carry a Reserved or Identifier badge. */
  identifiers: IdentifiersConfig | null | undefined;
  withAttributeBadges: boolean;
  /** The description, which the row's own editor renders. */
  description: ReactNode;
};

export function NameCell({
  name,
  href,
  isSensitive,
  isAutogenerated,
  identifiers,
  withAttributeBadges,
  description,
}: NameCellProps) {
  return (
    <Stack>
      <InlineStack alignBlock="center" gap="075">
        <RowClickGuard>
          <Link variant="primary" href={href} truncate>
            {name}
          </Link>
        </RowClickGuard>
        {isAutogenerated ? <AutogeneratedAttributeTooltip attribute={name} /> : null}
        {withAttributeBadges ? <AttributeTypeLabel attribute={name} identifiers={identifiers} /> : null}
        {isSensitive ? <Badge color="blue">Sensitive</Badge> : null}
      </InlineStack>
      <Box pt="050">{description}</Box>
    </Stack>
  );
}
